import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Locale = 'it' | 'en';

interface I18nContextType {
  locale: Locale;
  t: (key: string) => string;
  changeLocale: (locale: Locale) => void;
  translations: typeof translations;
}

const translations = {
  it: {
    common: {
      loading: 'Caricamento...',
      save: 'Salva',
      cancel: 'Annulla',
      delete: 'Elimina',
      edit: 'Modifica',
      add: 'Aggiungi',
      search: 'Cerca',
      filter: 'Filtra',
      all: 'Tutti',
      none: 'Nessuno',
      yes: 'Sì',
      no: 'No',
      confirm: 'Conferma',
      back: 'Indietro',
      next: 'Avanti',
      submit: 'Invia',
      close: 'Chiudi',
      actions: 'Azioni',
      details: 'Dettagli',
      status: 'Stato',
      date: 'Data',
      total: 'Totale',
      quantity: 'Quantità',
      price: 'Prezzo',
      unit: 'Unità',
      notes: 'Note',
      description: 'Descrizione',
      name: 'Nome',
      email: 'Email',
      phone: 'Telefono',
      address: 'Indirizzo',
      category: 'Categoria',
      type: 'Tipo',
      error: 'Errore',
      success: 'Successo',
      warning: 'Attenzione',
      errorLoading: 'Errore nel caricamento',
      errorSaving: 'Errore nel salvataggio',
      errorDeleting: 'Errore nell\'eliminazione',
      savedSuccess: 'Salvato con successo',
      deletedSuccess: 'Eliminato con successo',
      createdSuccess: 'Creato con successo',
      updatedSuccess: 'Aggiornato con successo',
      requiredField: 'Campo obbligatorio',
      invalidValue: 'Valore non valido',
      selectOption: 'Seleziona un\'opzione',
    },
    auth: {
      login: 'Accedi',
      register: 'Registrati',
      logout: 'Esci',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Conferma Password',
      forgotPassword: 'Password dimenticata?',
      noAccount: 'Non hai un account?',
      hasAccount: 'Hai già un account?',
      loginTitle: 'Bentornato',
      loginSubtitle: 'Accedi al tuo account',
      registerTitle: 'Crea Account',
      registerSubtitle: 'Inizia a digitalizzare i tuoi ordini',
      iAmBuyer: 'Sono un Acquirente',
      iAmSeller: 'Sono un Fornitore',
      buyerDesc: 'Ristoratore, dettagliante, pubblico esercizio',
      sellerDesc: 'Produttore, distributore, rappresentante',
      companyName: 'Nome Azienda',
      companyType: 'Tipo Azienda',
      yourName: 'Il tuo Nome',
    },
    nav: {
      dashboard: 'Dashboard',
      orders: 'Ordini',
      directory: 'Directory Fornitori',
      catalog: 'Catalogo',
      pricelists: 'Listini',
      relationships: 'Relazioni',
      chat: 'Messaggi',
      settings: 'Impostazioni',
      profile: 'Profilo',
    },
    dashboard: {
      welcome: 'Bentornato',
      overview: 'Panoramica',
      totalOrders: 'Ordini Totali',
      pendingOrders: 'Ordini in Attesa',
      activeRelationships: 'Relazioni Attive',
      pendingRelationships: 'Richieste in Attesa',
      totalProducts: 'Prodotti',
      recentOrders: 'Ordini Recenti',
      quickActions: 'Azioni Rapide',
      newOrder: 'Nuovo Ordine',
      viewCatalog: 'Vedi Catalogo',
      manageProducts: 'Gestisci Prodotti',
      topSellingProducts: 'Prodotti più Venduti',
      topOrderedProducts: 'Prodotti più Ordinati',
      last30Days: 'Ultimi 30 giorni',
      unitsSold: 'unità vendute',
      unitsOrdered: 'unità ordinate',
    },
    orders: {
      title: 'Ordini',
      newOrder: 'Nuovo Ordine',
      orderNumber: 'Ordine #',
      orderDate: 'Data Ordine',
      deliveryDate: 'Data Consegna',
      supplier: 'Fornitore',
      customer: 'Cliente',
      items: 'Articoli',
      subtotal: 'Subtotale',
      draft: 'Bozza',
      submitted: 'Inviato',
      confirmed: 'Confermato',
      in_preparation: 'In Preparazione',
      ready: 'Pronto',
      shipped: 'Spedito',
      in_delivery: 'In Consegna',
      delivered: 'Consegnato',
      completed: 'Completato',
      cancelled: 'Annullato',
      submitOrder: 'Invia Ordine',
      confirmOrder: 'Conferma Ordine',
      reorder: 'Riordina',
      noOrders: 'Nessun ordine trovato',
      orderDetails: 'Dettagli Ordine',
      addItem: 'Aggiungi Articolo',
      selectProduct: 'Seleziona Prodotto',
      filterByDate: 'Filtra per data',
      dateFrom: 'Data da',
      dateTo: 'Data a',
      removeDateFilter: 'Rimuovi filtro date',
      resetFilters: 'Rimuovi filtri',
    },
    directory: {
      title: 'Directory Fornitori',
      searchPlaceholder: 'Cerca fornitori...',
      allCategories: 'Tutte le Categorie',
      allTypes: 'Tutti i Tipi',
      producer: 'Produttore',
      distributor: 'Distributore',
      agent: 'Agente',
      other: 'Altro',
      noResults: 'Nessun fornitore trovato',
      requestRelation: 'Richiedi Relazione',
      requestAccess: 'Richiedi Accesso Catalogo',
      viewProfile: 'Vedi Profilo',
      accredited: 'Accreditato',
      requestSent: 'Richiesta inviata! Il fornitore valuterà la tua richiesta.',
      allSuppliers: 'Tutti i Fornitori',
      mySuppliers: 'I Miei Fornitori',
      pendingRequests: 'In Attesa',
      active: 'Attivo',
      pendingEvaluation: 'In Valutazione',
      notConnected: 'Non Connesso',
    },
    catalog: {
      title: 'Catalogo Prodotti',
      addProduct: 'Aggiungi Prodotto',
      editProduct: 'Modifica Prodotto',
      productName: 'Nome Prodotto',
      productNameEn: 'Nome Prodotto (EN)',
      sku: 'Codice Articolo',
      unitOfMeasure: 'Unità di Misura',
      noProducts: 'Nessun prodotto nel catalogo',
      importCatalog: 'Importa Catalogo',
      newCatalog: 'Nuovo Catalogo',
      editCatalog: 'Modifica Catalogo',
      catalogName: 'Nome Catalogo',
      catalogCreated: 'Catalogo creato',
      catalogUpdated: 'Catalogo aggiornato',
      catalogDeleted: 'Catalogo eliminato',
      cannotDeleteDefault: 'Non puoi eliminare il catalogo predefinito',
      selectProducts: 'Seleziona almeno un prodotto',
      productsAdded: 'Prodotti aggiunti al catalogo',
      productRemoved: 'Prodotto rimosso dal catalogo',
      goToCatalog: 'Vai al Catalogo',
    },
    pricelists: {
      title: 'Listini Prezzi',
      standardList: 'Listino Standard',
      customList: 'Listino Personalizzato',
      addList: 'Nuovo Listino',
      assignedTo: 'Assegnato a',
      itemCount: 'Articoli',
      addItem: 'Aggiungi Prezzo',
      minQuantity: 'Quantità Minima',
      priceHistory: 'Storico Prezzi',
      customCreated: 'Listino personalizzato creato',
      priceUpdated: 'Prezzo aggiornato',
      productAdded: 'Prodotto aggiunto al listino',
      productRemoved: 'Prodotto rimosso dal listino',
      selectProduct: 'Seleziona un prodotto',
      enterValidPrice: 'Inserisci un prezzo valido',
      basePricelist: 'Listino Base',
      createCustom: 'Crea Listino Personalizzato',
    },
    relationships: {
      title: 'Relazioni Commerciali',
      pending: 'In Attesa',
      active: 'Attive',
      accepted: 'Accettato',
      rejected: 'Rifiutato',
      paused: 'In Pausa',
      terminated: 'Terminato',
      accept: 'Accetta',
      reject: 'Rifiuta',
      assignPricelist: 'Assegna Listino',
      noRelationships: 'Nessuna relazione',
      requestSent: 'Richiesta inviata',
      waitingApproval: 'In attesa di approvazione',
    },
    chat: {
      title: 'Messaggi',
      noConversations: 'Nessuna conversazione',
      typeMessage: 'Scrivi un messaggio...',
      send: 'Invia',
      aiAssistant: 'Assistente AI',
      createOrderFromChat: 'Crea Ordine da Chat',
      parseOrder: 'Analizza Ordine',
      errorSending: 'Errore nell\'invio del messaggio',
      errorUploading: 'Errore nel caricamento del file',
      draftGenerated: 'Bozza ordine generata',
      orderCreated: 'Ordine creato con successo',
      attachFile: 'Allega file',
      downloadAttachment: 'Scarica allegato',
    },
    settings: {
      title: 'Impostazioni',
      description: 'Gestisci il tuo profilo e le preferenze',
      profile: 'Profilo',
      profileDescription: 'I tuoi dati personali',
      company: 'Azienda',
      preferences: 'Preferenze',
      preferencesDescription: 'Personalizza l\'aspetto dell\'applicazione',
      language: 'Lingua',
      theme: 'Tema',
      lightTheme: 'Chiaro',
      darkTheme: 'Scuro',
      primaryColor: 'Colore Primario',
      notifications: 'Notifiche',
      notificationsDescription: 'Configura come ricevere le notifiche',
      team: 'Team',
      stock: 'Gestione Stock',
      stockManagement: 'Gestione Magazzino',
      stockManagementDescription: 'Configura le impostazioni del magazzino',
      enableStock: 'Abilita Gestione Stock',
      enableStockDescription: 'Attiva il tracciamento delle giacenze per i tuoi prodotti',
      lowStockThreshold: 'Soglia Stock Basso',
      stockDisplayMode: 'Modalità Visualizzazione',
      allowBackorder: 'Consenti Backorder',
      reserveOnOrder: 'Riserva su Ordine',
      changePassword: 'Cambia Password',
      changePasswordDescription: 'Aggiorna la tua password di accesso',
      currentPassword: 'Password Attuale',
      newPassword: 'Nuova Password',
      confirmPassword: 'Conferma Password',
      updatePassword: 'Aggiorna Password',
      accreditation: 'Accreditamento',
      requestAccreditation: 'Richiedi Accreditamento',
      accreditationApproved: 'Accreditamento approvato',
      accreditationRejected: 'Accreditamento rifiutato',
      accreditationRevoked: 'Accreditamento revocato',
      logoUpdated: 'Logo aggiornato',
      logoRemoved: 'Logo rimosso',
      savedSuccess: 'Impostazioni salvate',
    },
    company: {
      name: 'Nome Azienda',
      vatNumber: 'Partita IVA',
      address: 'Indirizzo',
      phone: 'Telefono',
      email: 'Email',
    },
  },
  en: {
    common: {
      loading: 'Loading...',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      none: 'None',
      yes: 'Yes',
      no: 'No',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      submit: 'Submit',
      close: 'Close',
      actions: 'Actions',
      details: 'Details',
      status: 'Status',
      date: 'Date',
      total: 'Total',
      quantity: 'Quantity',
      price: 'Price',
      unit: 'Unit',
      notes: 'Notes',
      description: 'Description',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      category: 'Category',
      type: 'Type',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      errorLoading: 'Error loading data',
      errorSaving: 'Error saving',
      errorDeleting: 'Error deleting',
      savedSuccess: 'Saved successfully',
      deletedSuccess: 'Deleted successfully',
      createdSuccess: 'Created successfully',
      updatedSuccess: 'Updated successfully',
      requiredField: 'Required field',
      invalidValue: 'Invalid value',
      selectOption: 'Select an option',
    },
    auth: {
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to your account',
      registerTitle: 'Create Account',
      registerSubtitle: 'Start digitizing your orders',
      iAmBuyer: "I'm a Buyer",
      iAmSeller: "I'm a Supplier",
      buyerDesc: 'Restaurant, retailer, hospitality business',
      sellerDesc: 'Producer, distributor, sales agent',
      companyName: 'Company Name',
      companyType: 'Company Type',
      yourName: 'Your Name',
    },
    nav: {
      dashboard: 'Dashboard',
      orders: 'Orders',
      directory: 'Supplier Directory',
      catalog: 'Catalog',
      pricelists: 'Price Lists',
      relationships: 'Relationships',
      chat: 'Messages',
      settings: 'Settings',
      profile: 'Profile',
    },
    dashboard: {
      welcome: 'Welcome back',
      overview: 'Overview',
      totalOrders: 'Total Orders',
      pendingOrders: 'Pending Orders',
      activeRelationships: 'Active Relationships',
      pendingRelationships: 'Pending Requests',
      totalProducts: 'Products',
      recentOrders: 'Recent Orders',
      quickActions: 'Quick Actions',
      newOrder: 'New Order',
      viewCatalog: 'View Catalog',
      manageProducts: 'Manage Products',
      topSellingProducts: 'Top Selling Products',
      topOrderedProducts: 'Most Ordered Products',
      last30Days: 'Last 30 days',
      unitsSold: 'units sold',
      unitsOrdered: 'units ordered',
    },
    orders: {
      title: 'Orders',
      newOrder: 'New Order',
      orderNumber: 'Order #',
      orderDate: 'Order Date',
      deliveryDate: 'Delivery Date',
      supplier: 'Supplier',
      customer: 'Customer',
      items: 'Items',
      subtotal: 'Subtotal',
      draft: 'Draft',
      submitted: 'Submitted',
      confirmed: 'Confirmed',
      in_preparation: 'In Preparation',
      ready: 'Ready',
      shipped: 'Shipped',
      in_delivery: 'In Delivery',
      delivered: 'Delivered',
      completed: 'Completed',
      cancelled: 'Cancelled',
      submitOrder: 'Submit Order',
      confirmOrder: 'Confirm Order',
      reorder: 'Reorder',
      noOrders: 'No orders found',
      orderDetails: 'Order Details',
      addItem: 'Add Item',
      selectProduct: 'Select Product',
      filterByDate: 'Filter by date',
      dateFrom: 'Date from',
      dateTo: 'Date to',
      removeDateFilter: 'Remove date filter',
      resetFilters: 'Clear filters',
    },
    directory: {
      title: 'Supplier Directory',
      searchPlaceholder: 'Search suppliers...',
      allCategories: 'All Categories',
      allTypes: 'All Types',
      producer: 'Producer',
      distributor: 'Distributor',
      agent: 'Agent',
      other: 'Other',
      noResults: 'No suppliers found',
      requestRelation: 'Request Relationship',
      requestAccess: 'Request Catalog Access',
      viewProfile: 'View Profile',
      accredited: 'Accredited',
      requestSent: 'Request sent! The supplier will review your request.',
      allSuppliers: 'All Suppliers',
      mySuppliers: 'My Suppliers',
      pendingRequests: 'Pending',
      active: 'Active',
      pendingEvaluation: 'Pending Evaluation',
      notConnected: 'Not Connected',
    },
    catalog: {
      title: 'Product Catalog',
      addProduct: 'Add Product',
      editProduct: 'Edit Product',
      productName: 'Product Name',
      productNameEn: 'Product Name (EN)',
      sku: 'Item Code',
      unitOfMeasure: 'Unit of Measure',
      noProducts: 'No products in catalog',
      importCatalog: 'Import Catalog',
      newCatalog: 'New Catalog',
      editCatalog: 'Edit Catalog',
      catalogName: 'Catalog Name',
      catalogCreated: 'Catalog created',
      catalogUpdated: 'Catalog updated',
      catalogDeleted: 'Catalog deleted',
      cannotDeleteDefault: 'Cannot delete default catalog',
      selectProducts: 'Select at least one product',
      productsAdded: 'Products added to catalog',
      productRemoved: 'Product removed from catalog',
      goToCatalog: 'Go to Catalog',
    },
    pricelists: {
      title: 'Price Lists',
      standardList: 'Standard Price List',
      customList: 'Custom Price List',
      addList: 'New Price List',
      assignedTo: 'Assigned to',
      itemCount: 'Items',
      addItem: 'Add Price',
      minQuantity: 'Minimum Quantity',
      priceHistory: 'Price History',
      customCreated: 'Custom price list created',
      priceUpdated: 'Price updated',
      productAdded: 'Product added to price list',
      productRemoved: 'Product removed from price list',
      selectProduct: 'Select a product',
      enterValidPrice: 'Enter a valid price',
      basePricelist: 'Base Price List',
      createCustom: 'Create Custom Price List',
    },
    relationships: {
      title: 'Business Relationships',
      pending: 'Pending',
      active: 'Active',
      accepted: 'Accepted',
      rejected: 'Rejected',
      paused: 'Paused',
      terminated: 'Terminated',
      accept: 'Accept',
      reject: 'Reject',
      assignPricelist: 'Assign Price List',
      noRelationships: 'No relationships',
      requestSent: 'Request sent',
      waitingApproval: 'Waiting for approval',
    },
    chat: {
      title: 'Messages',
      noConversations: 'No conversations',
      typeMessage: 'Type a message...',
      send: 'Send',
      aiAssistant: 'AI Assistant',
      createOrderFromChat: 'Create Order from Chat',
      parseOrder: 'Parse Order',
      errorSending: 'Error sending message',
      errorUploading: 'Error uploading file',
      draftGenerated: 'Order draft generated',
      orderCreated: 'Order created successfully',
      attachFile: 'Attach file',
      downloadAttachment: 'Download attachment',
    },
    settings: {
      title: 'Settings',
      description: 'Manage your profile and preferences',
      profile: 'Profile',
      profileDescription: 'Your personal information',
      company: 'Company',
      preferences: 'Preferences',
      preferencesDescription: 'Customize the app appearance',
      language: 'Language',
      theme: 'Theme',
      lightTheme: 'Light',
      darkTheme: 'Dark',
      primaryColor: 'Primary Color',
      notifications: 'Notifications',
      notificationsDescription: 'Configure how to receive notifications',
      team: 'Team',
      stock: 'Stock Management',
      stockManagement: 'Stock Management',
      stockManagementDescription: 'Configure inventory settings',
      enableStock: 'Enable Stock Management',
      enableStockDescription: 'Track inventory for your products',
      lowStockThreshold: 'Low Stock Threshold',
      stockDisplayMode: 'Display Mode',
      allowBackorder: 'Allow Backorder',
      reserveOnOrder: 'Reserve on Order',
      changePassword: 'Change Password',
      changePasswordDescription: 'Update your login password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      updatePassword: 'Update Password',
      accreditation: 'Accreditation',
      requestAccreditation: 'Request Accreditation',
      accreditationApproved: 'Accreditation approved',
      accreditationRejected: 'Accreditation rejected',
      accreditationRevoked: 'Accreditation revoked',
      logoUpdated: 'Logo updated',
      logoRemoved: 'Logo removed',
      savedSuccess: 'Settings saved',
    },
    company: {
      name: 'Company Name',
      vatNumber: 'VAT Number',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
    },
  },
} as const;

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState<Locale>(() => {
    const stored = localStorage.getItem('horecaplat_locale');
    return (stored === 'it' || stored === 'en') ? stored : 'it';
  });

  useEffect(() => {
    localStorage.setItem('horecaplat_locale', locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[locale];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const changeLocale = (newLocale: Locale) => {
    if (translations[newLocale]) {
      setLocale(newLocale);
    }
  };

  return (
    <I18nContext.Provider value={{ locale, t, changeLocale, translations }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};
